import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Textarea } from '../../components/ui/Textarea';
import { Modal } from '../../components/ui/Modal';
import {
  Settings, Save, RefreshCw, TestTube, AlertTriangle, CheckCircle, XCircle, Plus, Edit, Trash2, Eye, EyeOff, Key, Globe, Database, Mail, Phone, Zap, Shield, Activity, Link, Lock, Clock, RotateCcw, FileText, Info
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const IntegrationSettings = () => {
  const [settings, setSettings] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'api',
    endpoint: '',
    apiKey: '',
    secret: '',
    timeout: 30,
    retries: 3,
    enabled: true,
    description: ''
  });
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [settingsResponse, integrationsResponse] = await Promise.all([
          MockDataService.getIntegrationSettings(),
          MockDataService.getIntegrations()
        ]);

        if (settingsResponse.success) {
          setSettings(settingsResponse.data);
        }
        if (integrationsResponse.success) {
          setIntegrations(integrationsResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch integration settings data.");
        console.error("Error fetching integration settings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.updateIntegrationSettings(settings);
      if (response.success) {
        setSettings(response.data);
        alert('Integration settings saved successfully!');
      } else {
        alert('Failed to save integration settings');
      }
    } catch (err) {
      alert('Failed to save integration settings');
      console.error("Error saving settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateIntegration = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.createIntegration(formData);
      if (response.success) {
        setIntegrations(prev => [...prev, response.data]);
        setShowCreateModal(false);
        setFormData({
          name: '',
          type: 'api',
          endpoint: '',
          apiKey: '',
          secret: '',
          timeout: 30,
          retries: 3,
          enabled: true,
          description: ''
        });
        alert('Integration created successfully!');
      } else {
        alert('Failed to create integration');
      }
    } catch (err) {
      alert('Failed to create integration');
      console.error("Error creating integration:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditIntegration = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.updateIntegration(selectedIntegration.id, formData);
      if (response.success) {
        setIntegrations(prev => prev.map(integration => 
          integration.id === selectedIntegration.id ? response.data : integration
        ));
        setShowEditModal(false);
        setSelectedIntegration(null);
        alert('Integration updated successfully!');
      } else {
        alert('Failed to update integration');
      }
    } catch (err) {
      alert('Failed to update integration');
      console.error("Error updating integration:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      try {
        const response = await MockDataService.deleteIntegration(integrationId);
        if (response.success) {
          setIntegrations(prev => prev.filter(integration => integration.id !== integrationId));
          alert('Integration deleted successfully!');
        } else {
          alert('Failed to delete integration');
        }
      } catch (err) {
        alert('Failed to delete integration');
        console.error("Error deleting integration:", err);
      }
    }
  };

  const handleTestIntegration = async (integration) => {
    try {
      setTesting(true);
      setSelectedIntegration(integration);
      setShowTestModal(true);
      
      const response = await MockDataService.testIntegration(integration.id);
      if (response.success) {
        setTestResults(response.data);
      } else {
        setTestResults({ success: false, message: response.message });
      }
    } catch (err) {
      setTestResults({ success: false, message: 'Test failed' });
      console.error("Error testing integration:", err);
    } finally {
      setTesting(false);
    }
  };

  const handleToggleIntegration = async (integrationId) => {
    try {
      const integration = integrations.find(i => i.id === integrationId);
      const response = await MockDataService.updateIntegration(integrationId, {
        ...integration,
        enabled: !integration.enabled
      });
      
      if (response.success) {
        setIntegrations(prev => prev.map(integration => 
          integration.id === integrationId ? response.data : integration
        ));
      }
    } catch (err) {
      alert('Failed to toggle integration status');
      console.error("Error toggling integration:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'error': return 'destructive';
      case 'warning': return 'outline';
      default: return 'outline';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'api': return <Globe className="h-4 w-4" />;
      case 'webhook': return <Zap className="h-4 w-4" />;
      case 'database': return <Database className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <Phone className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  if (loading) return <div className="p-4 text-center">Loading Integration Settings...</div>;
  if (error) return <div className="p-4 text-center text-[#BB0000]">Error: {error}</div>;

  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Integration Settings</h1>
          <p className="text-muted-foreground">Configure and manage system integrations.</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Integration
          </Button>
          <Button onClick={handleSaveSettings} disabled={saving}>
            <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Global Settings */}
      {settings && (
        <Card>
          <CardHeader>
            <CardTitle>Global Integration Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Default Timeout (seconds)</label>
                <Input
                  type="number"
                  value={settings.defaultTimeout}
                  onChange={(e) => setSettings({...settings, defaultTimeout: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Retries</label>
                <Input
                  type="number"
                  value={settings.maxRetries}
                  onChange={(e) => setSettings({...settings, maxRetries: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Rate Limit (requests/minute)</label>
                <Input
                  type="number"
                  value={settings.rateLimit}
                  onChange={(e) => setSettings({...settings, rateLimit: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Enable Logging</label>
                <select
                  value={settings.enableLogging ? 'true' : 'false'}
                  onChange={(e) => setSettings({...settings, enableLogging: e.target.value === 'true'})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Webhook Base URL</label>
              <Input
                type="url"
                value={settings.webhookBaseUrl}
                onChange={(e) => setSettings({...settings, webhookBaseUrl: e.target.value})}
                placeholder="https://your-domain.com/webhooks"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Integrations List */}
      <Card>
        <CardHeader>
          <CardTitle>Configured Integrations ({integrations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {integrations.map((integration) => (
              <div key={integration.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getTypeIcon(integration.type)}
                      <h3 className="text-lg font-semibold">{integration.name}</h3>
                      <Badge variant={getStatusColor(integration.status)}>
                        {integration.status}
                      </Badge>
                      <Badge variant={integration.enabled ? 'success' : 'secondary'}>
                        {integration.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{integration.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Globe className="h-4 w-4 mr-1" />
                        {integration.endpoint}
                      </span>
                      <span className="flex items-center">
                        <Activity className="h-4 w-4 mr-1" />
                        {integration.type}
                      </span>
                      <span className="flex items-center">
                        <Shield className="h-4 w-4 mr-1" />
                        {integration.securityLevel}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestIntegration(integration)}
                      disabled={testing}
                    >
                      <TestTube className="h-4 w-4 mr-1" />
                      Test
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedIntegration(integration);
                        setFormData(integration);
                        setShowEditModal(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleIntegration(integration.id)}
                    >
                      {integration.enabled ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {integration.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteIntegration(integration.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Integration Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <div className="max-w-3xl -m-6">
          {/* Modal Header */}
          <div className="gradient-hero px-6 py-5 rounded-t-lg relative">
            <button
              onClick={() => setShowCreateModal(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              aria-label="Close"
            >
              <XCircle className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-3 pr-12">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Plus className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Create New Integration</h2>
                <p className="text-white/90 text-sm mt-1">Connect your external service to the helpdesk</p>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <Info className="h-5 w-5" style={{ color: '#006600' }} />
                <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <FileText className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                    Name <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Salesforce CRM Integration"
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                  />
                  <p className="text-xs text-muted-foreground">A descriptive name for this integration</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Zap className="h-4 w-4 mr-2" style={{ color: '#BB0000' }} />
                    Type <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="w-full h-11 px-4 pr-10 border-2 rounded-md appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-medium"
                      style={{ borderColor: '#e0e0e0' }}
                    >
                      <option value="api">API Integration</option>
                      <option value="webhook">Webhook</option>
                      <option value="database">Database</option>
                      <option value="email">Email Service</option>
                      <option value="sms">SMS Service</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Select the integration type</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-semibold text-foreground">
                  <Link className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                  Endpoint URL <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  value={formData.endpoint}
                  onChange={(e) => setFormData({...formData, endpoint: e.target.value})}
                  placeholder="https://api.example.com"
                  className="border-2 h-11 font-mono text-sm"
                  style={{ borderColor: '#e0e0e0' }}
                />
                <p className="text-xs text-muted-foreground">The base URL for API requests</p>
              </div>
            </div>

            {/* Security Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <Shield className="h-5 w-5" style={{ color: '#BB0000' }} />
                <h3 className="text-lg font-semibold text-foreground">Security Credentials</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Key className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                    API Key
                  </Label>
                  <Input
                    type="password"
                    value={formData.apiKey}
                    onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
                    placeholder="Enter your API key"
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                  />
                  <p className="text-xs text-muted-foreground">Your API authentication key</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Lock className="h-4 w-4 mr-2" style={{ color: '#BB0000' }} />
                    Secret
                  </Label>
                  <Input
                    type="password"
                    value={formData.secret}
                    onChange={(e) => setFormData({...formData, secret: e.target.value})}
                    placeholder="Enter your secret key"
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                  />
                  <p className="text-xs text-muted-foreground">Your API secret key</p>
                </div>
              </div>
            </div>

            {/* Advanced Configuration Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <Settings className="h-5 w-5" style={{ color: '#000000' }} />
                <h3 className="text-lg font-semibold text-foreground">Advanced Configuration</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Clock className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                    Timeout (seconds)
                  </Label>
                  <Input
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => setFormData({...formData, timeout: parseInt(e.target.value)})}
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                    min="1"
                    max="300"
                  />
                  <p className="text-xs text-muted-foreground">Request timeout duration</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <RotateCcw className="h-4 w-4 mr-2" style={{ color: '#BB0000' }} />
                    Retries
                  </Label>
                  <Input
                    type="number"
                    value={formData.retries}
                    onChange={(e) => setFormData({...formData, retries: parseInt(e.target.value)})}
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                    min="0"
                    max="10"
                  />
                  <p className="text-xs text-muted-foreground">Number of retry attempts</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-semibold text-foreground">
                  <FileText className="h-4 w-4 mr-2" style={{ color: '#000000' }} />
                  Description
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the purpose and usage of this integration..."
                  rows={3}
                  className="border-2 resize-none"
                  style={{ borderColor: '#e0e0e0' }}
                />
                <p className="text-xs text-muted-foreground">Optional description for documentation</p>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-[#F8FCFB] rounded-lg border border-border">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                  className="w-5 h-5 rounded border-2 border-border text-[#006600] focus:ring-2 focus:ring-ring"
                />
                <label htmlFor="enabled" className="flex items-center text-sm font-semibold text-foreground cursor-pointer">
                  <Activity className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                  Enable integration immediately after creation
                </label>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 bg-[#F8FCFB] rounded-b-lg border-t border-border flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="px-6 py-3 border-2 hover:bg-[#E0F8F3] transition-all duration-200"
              style={{ borderColor: '#e0e0e0' }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleCreateIntegration}
              disabled={saving}
              className="px-6 py-3 text-white transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              style={{
                background: saving ? 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)' : 'linear-gradient(135deg, #006600 0%, #006600 100%)',
                border: 'none'
              }}
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Integration
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Integration Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
        <div className="max-w-3xl -m-6">
          {/* Modal Header */}
          <div className="gradient-hero px-6 py-5 rounded-t-lg relative">
            <button
              onClick={() => setShowEditModal(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              aria-label="Close"
            >
              <XCircle className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-3 pr-12">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Edit className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Edit Integration</h2>
                <p className="text-white/90 text-sm mt-1">Update integration configuration and settings</p>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <Info className="h-5 w-5" style={{ color: '#006600' }} />
                <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <FileText className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                    Name <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Salesforce CRM Integration"
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Zap className="h-4 w-4 mr-2" style={{ color: '#BB0000' }} />
                    Type <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="w-full h-11 px-4 pr-10 border-2 rounded-md appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-medium"
                      style={{ borderColor: '#e0e0e0' }}
                    >
                      <option value="api">API Integration</option>
                      <option value="webhook">Webhook</option>
                      <option value="database">Database</option>
                      <option value="email">Email Service</option>
                      <option value="sms">SMS Service</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-semibold text-foreground">
                  <Link className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                  Endpoint URL <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  value={formData.endpoint}
                  onChange={(e) => setFormData({...formData, endpoint: e.target.value})}
                  placeholder="https://api.example.com"
                  className="border-2 h-11 font-mono text-sm"
                  style={{ borderColor: '#e0e0e0' }}
                />
              </div>
            </div>

            {/* Security Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <Shield className="h-5 w-5" style={{ color: '#BB0000' }} />
                <h3 className="text-lg font-semibold text-foreground">Security Credentials</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Key className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                    API Key
                  </Label>
                  <Input
                    type="password"
                    value={formData.apiKey}
                    onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
                    placeholder="Enter your API key"
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Lock className="h-4 w-4 mr-2" style={{ color: '#BB0000' }} />
                    Secret
                  </Label>
                  <Input
                    type="password"
                    value={formData.secret}
                    onChange={(e) => setFormData({...formData, secret: e.target.value})}
                    placeholder="Enter your secret key"
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                  />
                </div>
              </div>
            </div>

            {/* Advanced Configuration Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <Settings className="h-5 w-5" style={{ color: '#000000' }} />
                <h3 className="text-lg font-semibold text-foreground">Advanced Configuration</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <Clock className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                    Timeout (seconds)
                  </Label>
                  <Input
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => setFormData({...formData, timeout: parseInt(e.target.value)})}
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                    min="1"
                    max="300"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center text-sm font-semibold text-foreground">
                    <RotateCcw className="h-4 w-4 mr-2" style={{ color: '#BB0000' }} />
                    Retries
                  </Label>
                  <Input
                    type="number"
                    value={formData.retries}
                    onChange={(e) => setFormData({...formData, retries: parseInt(e.target.value)})}
                    className="border-2 h-11"
                    style={{ borderColor: '#e0e0e0' }}
                    min="0"
                    max="10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-semibold text-foreground">
                  <FileText className="h-4 w-4 mr-2" style={{ color: '#000000' }} />
                  Description
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the purpose and usage of this integration..."
                  rows={3}
                  className="border-2 resize-none"
                  style={{ borderColor: '#e0e0e0' }}
                />
              </div>

              <div className="flex items-center space-x-3 p-4 bg-[#F8FCFB] rounded-lg border border-border">
                <input
                  type="checkbox"
                  id="editEnabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                  className="w-5 h-5 rounded border-2 border-border text-[#006600] focus:ring-2 focus:ring-ring"
                />
                <label htmlFor="editEnabled" className="flex items-center text-sm font-semibold text-foreground cursor-pointer">
                  <Activity className="h-4 w-4 mr-2" style={{ color: '#006600' }} />
                  Enable integration
                </label>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 bg-[#F8FCFB] rounded-b-lg border-t border-border flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="px-6 py-3 border-2 hover:bg-[#E0F8F3] transition-all duration-200"
              style={{ borderColor: '#e0e0e0' }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleEditIntegration}
              disabled={saving}
              className="px-6 py-3 text-white transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              style={{
                background: saving ? 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)' : 'linear-gradient(135deg, #006600 0%, #006600 100%)',
                border: 'none'
              }}
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Test Results Modal */}
      <Modal isOpen={showTestModal} onClose={() => setShowTestModal(false)}>
        <div className="max-w-2xl -m-6">
          {/* Modal Header */}
          <div className={`px-6 py-5 rounded-t-lg relative ${testResults?.success ? 'bg-[#006600]' : 'bg-[#BB0000]'}`}>
            <button
              onClick={() => setShowTestModal(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              aria-label="Close"
            >
              <XCircle className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-3 pr-12">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <TestTube className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Integration Test Results</h2>
                <p className="text-sm mt-1 text-white/90">
                  {testResults?.success ? 'Connection test completed successfully' : 'Connection test encountered errors'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-5">
            {testResults && (
              <div className="space-y-4">
                <div className={`p-5 rounded-lg border ${testResults.success ? 'bg-[#E8FCF7] border-border' : 'bg-[#FFE7E0] border-border'}`}>
                  <div className="flex items-center space-x-3 mb-4">
                    {testResults.success ? (
                      <CheckCircle className="h-6 w-6" style={{ color: '#006600' }} />
                    ) : (
                      <XCircle className="h-6 w-6" style={{ color: '#BB0000' }} />
                    )}
                    <span className="text-lg font-semibold" style={{ color: testResults.success ? '#006600' : '#BB0000' }}>
                      {testResults.success ? '✓ Test Passed' : '✗ Test Failed'}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: '#4A5B63' }}>
                    {testResults.message}
                  </p>
                  {testResults.details && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-sm mb-2 text-foreground">Technical Details:</h4>
                      <pre className="text-xs bg-white/50 p-4 rounded border border-border overflow-auto max-h-64 font-mono">
                        {JSON.stringify(testResults.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 bg-[#F8FCFB] rounded-b-lg border-t border-border flex justify-end">
            <Button
              onClick={() => setShowTestModal(false)}
              className="px-6 py-3 text-white transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              style={{
                background: testResults?.success ? 'linear-gradient(135deg, #006600 0%, #006600 100%)' : 'linear-gradient(135deg, #BB0000 0%, #BB0000 100%)',
                border: 'none'
              }}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default IntegrationSettings;

